/**
 * The introduction.
 *
 * A list of steps, each with a condition that advances it and a line of
 * dialogue. The flight loop already computes every condition here — distance,
 * mass lock, thruster set, gear, docked — so this is a lookup rather than a
 * simulation, and it can be tested by handing it a made-up situation.
 *
 * ── Why it is a list and not a graph ────────────────────────────────────────
 *
 * There is one branch in the whole thing, and it is a joke. Everything else is
 * strictly ordered because it is teaching controls in the order you need them:
 * you cannot be told to switch to precision thrusters before you are close
 * enough for it to mean anything. A state machine with one edge per node is a
 * list, and pretending otherwise would cost clarity for flexibility nothing is
 * asking for.
 */
import { keyLabel, pairLabel } from './input.js';
import { TUTORIAL_MISSION_REWARD } from './economy/missions.js';
/** Who is talking. The tutorial's voice, and the first character in the game. */
export const CONTROL_POST = 'Spacefare Control';
/**
 * How close counts as "on approach", in world units from the station's surface.
 *
 * ── Measured against the starter hull's actual braking distance ─────────────
 *
 * Assume what a new player really does: reads "get moving", pins the throttle,
 * and leaves it there. The starter accelerates at 6.4 u/s² and decelerates at
 * the same rate, so from a pinned 120 it needs **1,125 units and 18.8 seconds**
 * to come to rest. That is the number this has to clear, and both earlier
 * figures were guesses that did not: 350 was a tenth of it, and even 1,200 left
 * 75 units of margin for a pilot who reacted in zero seconds and did not have
 * to read anything first.
 *
 * 1,800 gives about 675 units — five and a half seconds — of slack on top of a
 * full-speed stop, which is the reading and reacting the line itself costs. It
 * fires around 1,200 units into a 3,000-unit run, so there is a real trip
 * before it and a real approach after it.
 */
export const TUTORIAL_APPROACH_RANGE = 1800;
/**
 * How far from the station a player has to get before the introduction gives up
 * on them, in world units from its surface.
 *
 * Someone who has flown eight thousand units out — nearly three times the
 * distance they were dropped at — has decided the introduction is not what they
 * are doing right now, and they are entitled to that. The alternative is Control
 * calling after them across the system about a landing pad they will never use,
 * which is what makes a tutorial feel like a leash.
 *
 * Far enough that it cannot fire by accident: overshooting on a bad first
 * approach is a few hundred units, not eight thousand.
 */
export const TUTORIAL_ABANDON_RANGE = 8000;
/**
 * The speed the slow-down step asks you to be under.
 *
 * Not the severe-impact threshold, which is what it used to be and which asked
 * for nothing at all: that constant is 120, and `CRUISE_SPEED` is *also* 120,
 * so "bring it under 120" was satisfied by any throttle short of pinned. You
 * cannot go fast enough to fail it outside warp.
 *
 * A third of cruise is an approach speed — slow enough that the last stretch is
 * flyable and the station stops being something you are aimed at.
 */
export const TUTORIAL_SAFE_SPEED = 20;
/**
 * The steps that are still a flight towards the first station.
 *
 * Only these can be abandoned by flying away, because only for these does
 * "flying away" mean anything — see `advanceTutorial`.
 */
const APPROACHING = new Set([
    'hail',
    'throttle',
    'approach',
    'slowDown',
    'precision',
    'gear',
    'land',
]);
const BEATS = {
    hail: {
        step: 'hail',
        line: 'Unidentified ship on approach, callsign: unknown. ' +
            'State your intent. Are you hostile? ' +
            `Hold [${keyLabel('cursor')}] to free your cursor, then answer me on the radio, top right. ` +
            '(Y/N)',
        awaitsReply: true,
    },
    throttle: {
        step: 'throttle',
        line: `Glad to hear it. Why dont you fly that thing over here and we can get you on record. ` +
            `you know how to fly that thing right? [${pairLabel('throttleUp', 'throttleDown')}] to get moving, ` +
            `mouse to look around. Harder to turn at speeds so don't ` +
            'be too hasty on the throttle.',
    },
    approach: {
        step: 'approach',
        line: 'Great, come on into the station, we\'ve prepaired a landing pad for you.',
    },
    slowDown: {
        step: 'slowDown',
        // Says *why* braking is slow, because the reason is not obvious and the
        // consequence of not knowing it is arriving at a wall. There is nothing out
        // here to slow you down, so cutting the throttle is not stopping — it is
        // only the moment you stop adding speed.
        line: 'Pull back on your throttle, it takes time to slow down in space and ' +
            'we dont want you to crash into the station.\n' +
            `Aim to be at about ${TUTORIAL_SAFE_SPEED} speed when you get here, ` +
            'then pull under 5 speed and you\'ll come to a nice stop',
    },
    precision: {
        step: 'precision',
        line: 'Your now mass locked, so you wont drift away from us. ' +
            `Switch to precision thrusters with [${keyLabel('thrusterSet')}] and come in for a landing.`,
    },
    gear: {
        step: 'gear',
        line: `Landing gear down with [${keyLabel('landingGear')}]. You'll bust up your ship otherwise.`,
    },
    deliver: {
        step: 'deliver',
        // Said by Control at the berth, which is where the player now is. The board
        // has exactly one job on it — see `tutorialBoard` — so this can be an
        // instruction rather than an explanation of how to choose.
        line: 'Pad locks are green, welcome in. There is one job on our board and it has ' +
            'your name on it: take the crate, fly it where the docket says, and hand it ' +
            `over there for ${TUTORIAL_MISSION_REWARD} linelits. ` +
            'No clock on this one, so take your time.',
    },
    land: {
        step: 'land',
        // Any small pad, not a reserved one. The flight loop offers whichever berth
        // you are actually over, so naming a specific plate would have Control
        // arguing with the ship about where you just landed.
        //
        // The rest of the controls are taught here rather than earlier because this
        // is the first moment they are worth anything. Strafing and roll are for
        // fine positioning, and the outside view is for seeing what your hull is
        // doing — none of which means a thing while crossing open space, and all of
        // which is exactly what lining up over a plate asks for.
        line: 'Take any small pad you like. Line up over the plate until the box goes ' +
            `green, then [${keyLabel('dock')}] to set down.
` +
            `If you're fighting it: [${pairLabel('strafeLeft', 'strafeRight')}] slide you sideways, ` +
            `[${pairLabel('up', 'down')}] lift and drop you, and ` +
            `[${pairLabel('rollLeft', 'rollRight')}] roll you level.
` +
            `Hold [${keyLabel('lookAround')}] to look about without turning the ship, and ` +
            `[${keyLabel('view')}] puts you outside the hull so you can see what you're doing.`,
    },
    clear: {
        step: 'clear',
        // Said the moment they leave the pad. Mass lock is the reason the drive
        // will not light, and a player who does not know that reads it as the ship
        // being broken.
        line: `As you leave, dont forget to disengage your landing gear [${keyLabel('landingGear')}]. ` +
            `and switch back to your Main Thrusters [${keyLabel('thrusterSet')}] once your clear ` +
            'that will help you escape the mass lock faster.',
    },
    raiseJump: {
        step: 'raiseJump',
        line: `You've pulled away now, engage your jump module with [${keyLabel('jump')}]. ` +
            'That will show your destination system on your hud.',
    },
    aimJump: {
        step: 'aimJump',
        line: 'Jump Module online. Turn until your destination sits under the reticle. ' +
            'Line it up with your ship direction. Bright markers are in ' +
            'reach; dim ones need a more powerful engine.',
    },
    warpUp: {
        step: 'warpUp',
        line: `Aim steady. Now throttle up and engage the warp drive with ` +
            `[${keyLabel('warp')}] once you hit 100 speed.`,
    },
    lockJump: {
        step: 'lockJump',
        line: `Warp Drive online. Keep steady and hold [${keyLabel('dock')}] ` +
            'to commit. Once committed theres no backing out.',
    },
    cruise: {
        step: 'cruise',
        line: `Crossing complete. Systems are big, so your ship is fitted with a cruise module to ` +
            `autopilot between stations and bodies. Line up with your destination and press [${keyLabel('cruise')}]. ` +
            'Its not the fastest but as long as you have a clear path, it gets you where your going, without crashing.',
    },
    done: {
        step: 'done',
        // never reaches, we are docked at this point
        line: 'VOID',
    },
    // Signed off rather than scolded. They have not failed anything — the game is
    // open and they went and played it, which is the correct instinct.
    abandoned: {
        step: 'abandoned',
        line: 'You are a long way out and still climbing, so I will leave you to it. ' +
            'Pad stays open if you want it. Spacefare Control, out.',
    },
};
/**
 * Lines that need to know who they are addressing.
 *
 * Two of eight: the hail, where Control reads your transponder, and the
 * welcome, where you go on the record. Written as overrides rather than by
 * threading a context through every line, because six of them do not care.
 *
 * The plain versions in `BEATS` are what a caller with no context gets — they
 * read sensibly rather than showing a gap, which matters because the tests and
 * the script dump both call it that way.
 */
const PERSONAL = {
    // Control reads your transponder before it has any idea who you are, which is
    // the joke in the line: it knows the callsign and asks anyway.
    // The pointer is locked to flying the moment you take the controls, so a
    // question you answer by typing is unanswerable until somebody says how. It
    // is the first instruction in the game because it is the first thing that
    // does not work if you have not been told.
    hail: ({ callsign }) => `Unidentified ship on approach, callsign: ${callsign}. ` +
        'State your intent. Are you hostile? ' +
        `Hold ${keyLabel('cursor')} to free your cursor, then answer me on the radio, top right. ` +
        '(Y/N)',
    // What to do next, and only that.
    //
    // It used to point at the trader, the mechanic and the yard, which is three
    // counters a new pilot cannot see: a berth opens with the mission board alone
    // and the rest appear as they earn their place — see `stationPanels.ts`. A
    // welcome that names two rooms that are not there is a welcome that reads as
    // broken, so it names the one that is, and the one that has their money in it.
    deliver: ({ callsign }) => `Pad locks are green, welcome in — you are on the record now, ${callsign}. ` +
        'There is one job on our board and it has your name on it: take the crate, fly ' +
        `it where the docket says, and hand it over there for ${TUTORIAL_MISSION_REWARD} ` +
        'linelits. No clock on this one, so take your time. The rest of the station ' +
        'opens up as you need it.',
    // Reached when that delivery is handed in, wherever that was.
    done: ({ callsign }) => `Paid and logged, ${callsign}. That is the whole of it — find work, fly it, ` +
        'get paid. Boards restock every couple of hours. Fly safe.',
};
export function beatFor(step, context) {
    const beat = BEATS[step];
    const personal = context ? PERSONAL[step] : undefined;
    return personal ? { ...beat, line: personal(context) } : beat;
}
/**
 * What the tutorial should be showing, given where the ship is.
 *
 * Returns the *next* step when the current one is satisfied, and the same step
 * when it is not. Never goes backwards: a player who drifts back out of mass
 * lock has still been told what mass lock is, and repeating it would be
 * nagging rather than teaching.
 */
export function advanceTutorial(step, situation) {
    if (step === 'done' || step === 'abandoned')
        return step;
    /*
     * Gone — but only while the introduction is still an approach.
     *
     * `distance` is measured to the station being flown *to*, which is the right
     * question right up until they land there. After that the introduction is a
     * delivery, and flying away from the station is the entire point: the run out
     * to a jump is thousands of units in the first few seconds, so leaving this
     * ungated would abandon the introduction for doing exactly what it just asked.
     *
     * Guarded on a finite distance as well: the flight loop reports `Infinity`
     * when it cannot find the station in the scene at all, and cancelling over a
     * transient lookup failure would be a strange way to learn about one.
     */
    if (APPROACHING.has(step) &&
        Number.isFinite(situation.distance) &&
        situation.distance > TUTORIAL_ABANDON_RANGE) {
        return 'abandoned';
    }
    switch (step) {
        // Only `N` moves this on. Saying yes is meant to send interceptors after
        // you, and that needs ship-to-ship collision and NPCs — neither of which
        // exists. Accepting it now would mean promising a consequence and then
        // quietly not delivering one, which is worse than not offering it.
        case 'hail':
            return situation.reply === 'N' ? 'throttle' : 'hail';
        // Moving at all is proof enough that the throttle was found.
        case 'throttle':
            return situation.speed > 5 ? 'approach' : 'throttle';
        case 'approach':
            return situation.distance <= TUTORIAL_APPROACH_RANGE ? 'slowDown' : 'approach';
        // Being caught is what moves this on, not merely being slow.
        //
        // It used to advance on `speed < TUTORIAL_SAFE_SPEED` as well, and that was
        // very nearly a no-op: the safe speed is the severe-impact threshold, 120,
        // and cruise is *also* 120 — so anything short of a pinned throttle already
        // satisfied it. The slow-down beat was skipped within a frame of arriving,
        // and the next line ("you're now mass locked") went out while the station
        // was still a thousand units away and had hold of nothing.
        //
        // Waiting for the lock costs a careful pilot nothing: they are already
        // slow, so they simply keep closing and it fires when it becomes true.
        case 'slowDown':
            return situation.massLocked ? 'precision' : 'slowDown';
        case 'precision':
            return situation.precision ? 'gear' : 'precision';
        case 'gear':
            return situation.gearDown ? 'land' : 'gear';
        // Docking is not the end any more. `deliver` is left for the mission board
        // to finish — no flight condition can see a crate being handed over — so
        // this step advances nowhere and the redemption clears it.
        case 'land':
            return situation.docked ? 'deliver' : 'land';
        /*
         * Off the pad *with the job*.
         *
         * Both halves matter. Leaving without taking it leaves the step where it is
         * — Control keeps asking, the board keeps offering — because the rest of the
         * introduction is about flying a delivery and there would be nothing to fly.
         */
        case 'deliver':
            return !situation.docked && situation.carryingJob === true ? 'clear' : 'deliver';
        case 'clear':
            return situation.massLocked ? 'clear' : 'raiseJump';
        case 'raiseJump':
            return situation.jumpUp === true ? 'aimJump' : 'raiseJump';
        case 'aimJump':
            return situation.jumpAimed === true ? 'warpUp' : 'aimJump';
        case 'warpUp':
            return situation.warping === true ? 'lockJump' : 'warpUp';
        /*
         * Held until the far side.
         *
         * Not "until the run starts": the crossing takes fifteen seconds of tunnel
         * during which the ship is out of the game, and a line about cruising that
         * arrived while the sky was still gas would be read by nobody. Arriving is
         * the moment the next instruction is worth anything.
         */
        case 'lockJump':
            return situation.atDestination === true ? 'cruise' : 'lockJump';
        /*
         * The last step, and nothing in flight ends it.
         *
         * Handing the crate over does, at a mission board, which this machine
         * cannot see — the redemption clears it server-side.
         */
        case 'cruise':
            return 'cruise';
        // No cases for `done` or `abandoned`: both returned at the top, and the
        // narrowing that follows from that is what keeps this switch exhaustive.
        // Adding a step to the union without handling it here is a compile error.
    }
}
/**
 * Whether this step is still the flight towards the first station.
 *
 * The introduction has two halves and they want opposite things from a berth:
 * before landing there is a particular station being talked towards, and after
 * it the player is an ordinary pilot going wherever the job says. Callers that
 * assign a destination need to know which half they are in.
 */
export function isApproaching(step) {
    return step !== null && step !== undefined && APPROACHING.has(step);
}
/**
 * Every step in the order it happens.
 *
 * Exported so that "which of these two is further along" can be answered
 * without anybody writing the sequence out a second time. `abandoned` is not in
 * it: it is an ending reached from anywhere rather than a position in the run.
 */
export const TUTORIAL_ORDER = [
    'hail',
    'throttle',
    'approach',
    'slowDown',
    'precision',
    'gear',
    'land',
    'deliver',
    'clear',
    'raiseJump',
    'aimJump',
    'warpUp',
    'lockJump',
    'cruise',
    'done',
];
/**
 * Whichever of two steps is further through the introduction.
 *
 * ── Why anything needs this ─────────────────────────────────────────────────
 *
 * The step moves forward in the *browser* — the flight loop is what can see a
 * ship leave a mass lock — and reaches the server on a debounced save a second
 * and a half later. Meanwhile every action answers with the whole stored record,
 * including a `tutorial` from before that save.
 *
 * So a reply can be *behind* what the client already knows, and adopting it
 * wholesale drags the introduction backwards. That is not a wobble it recovers
 * from: docking sets `deliver` locally and fires `/berth` in the same breath, so
 * the reply put the step back to `land` — and `land` advances on being docked,
 * which the flight loop never reports, so the introduction stopped there for
 * good. It looked exactly like taking the first job breaking the tutorial.
 *
 * An ending wins outright from either side. Everything else compares positions.
 */
export function laterTutorialStep(a, b) {
    // Over is over, and both spellings of it mean the same thing. A finished
    // introduction must never be restarted by a reply that predates finishing it.
    if (a === null || a === 'done' || a === 'abandoned')
        return a;
    if (b === null || b === 'done' || b === 'abandoned')
        return b;
    return TUTORIAL_ORDER.indexOf(b) > TUTORIAL_ORDER.indexOf(a) ? b : a;
}
/** Whether the tutorial has anything left to say. */
export function tutorialIsOver(step) {
    return step === 'done' || step === 'abandoned';
}
// ─── Where a new player starts ───────────────────────────────────────────────
/**
 * How far into the galaxy a first-time player is dropped, as a fraction of its
 * radius.
 *
 * The middle tenth. Everyone starts somewhere different, nobody starts in the
 * empty rim where the next station is a long haul in a starter hull — and
 * because the pool is small, new players are near enough each other to meet
 * without crossing the galaxy to do it.
 */
export const TUTORIAL_CORE_FRACTION = 0.1;
/**
 * How far from the station a new player wakes up, in world units.
 *
 * Far enough that flying there is a journey rather than a nudge. A thousand was
 * close enough to arrive at before the throttle lesson had finished being read,
 * which left the approach with nothing in it.
 */
export const TUTORIAL_SPAWN_RANGE = 3000;
/**
 * How far out of the station's plane a spawn may be, as a fraction of the range.
 *
 * Nearly flat. A point picked anywhere on a sphere is more interesting and much
 * less friendly: a player who has never flown would as often as not start
 * *underneath* the station with nothing in view, and their first act would be
 * hunting for it. Keeping the spawn near the orbital plane puts the station on
 * the horizon where a bracket can find it.
 */
export const TUTORIAL_SPAWN_TILT = 0.12;
/**
 * Where to put a new player, relative to the station.
 *
 * A bearing around the station and a slight lift out of plane, both seeded, so
 * two accounts start in different places and one account starts in the same
 * place every time it replays.
 */
export function tutorialSpawnOffset(random) {
    const bearing = random.float() * Math.PI * 2;
    const lift = (random.float() * 2 - 1) * TUTORIAL_SPAWN_TILT;
    // Normalised so the offset is the spawn range however far out of plane it
    // leans — otherwise a tilted spawn would be measurably further away.
    const flat = 1 / Math.sqrt(1 + lift * lift);
    return {
        x: Math.cos(bearing) * TUTORIAL_SPAWN_RANGE * flat,
        y: Math.sin(bearing) * TUTORIAL_SPAWN_RANGE * flat,
        z: lift * TUTORIAL_SPAWN_RANGE * flat,
    };
}
/**
 * How far off the sight line a new player may be facing, in radians.
 *
 * Fifteen degrees. Not zero: waking up pointed exactly at your destination is
 * a cutscene, and it also teaches nothing, because the first thing the game
 * should ask of you is to look around and find the thing you were told to fly
 * to. Fifteen is enough that the station is off-centre and you have to correct,
 * and little enough that it is already on screen at any sane field of view — a
 * player who has never flown should never be hunting for their target.
 */
export const TUTORIAL_SPAWN_CONE = (15 * Math.PI) / 180;
/** Rodrigues, for turning a direction a given angle about an axis. */
function spin(v, axis, angle) {
    const c = Math.cos(angle);
    const s = Math.sin(angle);
    const dot = axis.x * v.x + axis.y * v.y + axis.z * v.z;
    return {
        x: v.x * c + (axis.y * v.z - axis.z * v.y) * s + axis.x * dot * (1 - c),
        y: v.y * c + (axis.z * v.x - axis.x * v.z) * s + axis.y * dot * (1 - c),
        z: v.z * c + (axis.x * v.y - axis.y * v.x) * s + axis.z * dot * (1 - c),
    };
}
function normalise(v) {
    const length = Math.hypot(v.x, v.y, v.z);
    if (length === 0)
        return { x: 0, y: 1, z: 0 };
    return { x: v.x / length, y: v.y / length, z: v.z / length };
}
/**
 * Which way a new player is looking when they wake up.
 *
 * Roughly at the station: the spawn offset points *away* from it, so the sight
 * line is its negation, tilted by a random amount up to `TUTORIAL_SPAWN_CONE`.
 *
 * The tilt is a genuine cone rather than a wobble on each axis separately. Two
 * independent offsets make a square, which is both biased towards the corners
 * and — worse — never actually centred, because the odds of both being near
 * zero are small. Spinning one perpendicular to a random bearing gives every
 * direction round the sight line the same chance.
 */
export function tutorialSpawnFacing(offset, random) {
    const sight = normalise({ x: -offset.x, y: -offset.y, z: -offset.z });
    // Any vector not parallel to the sight line; the z axis unless we are looking
    // along it, in which case the x axis certainly is not.
    const aside = Math.abs(sight.z) > 0.9 ? { x: 1, y: 0, z: 0 } : { x: 0, y: 0, z: 1 };
    const perpendicular = normalise({
        x: sight.y * aside.z - sight.z * aside.y,
        y: sight.z * aside.x - sight.x * aside.z,
        z: sight.x * aside.y - sight.y * aside.x,
    });
    // Square-rooted so the tilt is spread evenly over the disc of directions
    // rather than bunched at the centre of it.
    const axis = spin(perpendicular, sight, random.float() * Math.PI * 2);
    return spin(sight, axis, TUTORIAL_SPAWN_CONE * Math.sqrt(random.float()));
}
/**
 * Pick a starting system for this account.
 *
 * The caller supplies the randomness and it is no longer seeded off the player:
 * an account bound permanently to one station meant replaying the introduction
 * always went back to the same berth. Requires a
 * station with a small pad, because the starter hull is small and a berth it
 * cannot use is not a lesson, it is a wall.
 *
 * The candidate list is supplied rather than derived here: `getGalaxyMap` and
 * `getSystem` live above this module in the dependency order, and passing the
 * shortlist in keeps the rule testable without generating a galaxy.
 */
export function pickTutorialStart(candidates, random) {
    if (candidates.length === 0)
        return null;
    return candidates[Math.floor(random.float() * candidates.length)] ?? candidates[0];
}
/**
 * Whether a system sits in the core.
 *
 * Distance from the galactic centre against the furthest system there is, so it
 * does not need to know how big the galaxy happens to be.
 */
export function isCoreSystem(distanceFromCentre, galaxyRadius) {
    return galaxyRadius > 0 && distanceFromCentre <= galaxyRadius * TUTORIAL_CORE_FRACTION;
}
//# sourceMappingURL=tutorial.js.map